import type {
    FC,
    InputHTMLAttributes,
    LabelHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

const CONTROL = 'w-full rounded-lg border border-input bg-card text-sm text-foreground shadow-xs transition-colors placeholder:text-muted-foreground/75 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-60 aria-invalid:border-destructive aria-invalid:ring-destructive/15'

export const Input: FC<InputHTMLAttributes<HTMLInputElement>> = ({ className, ...props }): ReactNode => {
    return <input className={cn(CONTROL, 'h-11 px-3.5', className)} {...props} />
}

export const Textarea: FC<TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className, ...props }): ReactNode => {
    return <textarea className={cn(CONTROL, 'min-h-28 px-3.5 py-3 leading-relaxed', className)} {...props} />
}

export const Select: FC<SelectHTMLAttributes<HTMLSelectElement>> = ({ className, children, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select className={cn(CONTROL, 'h-11 cursor-pointer appearance-none pl-3.5 pr-10', className)} {...props}>
                {children}
            </select>
            <ChevronDown className='pointer-events-none absolute right-3.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
        </div>
    )
}

export const Label: FC<LabelHTMLAttributes<HTMLLabelElement>> = ({ className, ...props }): ReactNode => {
    return <label className={cn('text-sm font-medium text-foreground', className)} {...props} />
}

interface FieldProps {
    label: string
    htmlFor: string
    hint?: string
    error?: string | null
    optional?: boolean
    className?: string
    children: ReactNode
}

export const Field: FC<FieldProps> = ({ label, htmlFor, hint, error, optional, className, children }): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-1.5', className)}>
            <Label htmlFor={htmlFor}>
                {label}
                {optional && <span className='ml-1.5 font-normal text-muted-foreground'>(optional)</span>}
            </Label>

            {children}

            {error
                ? <p className='text-sm text-destructive' role='alert'>{error}</p>
                : hint && <p className='text-sm text-muted-foreground'>{hint}</p>}
        </div>
    )
}

interface SwitchProps {
    id: string
    checked: boolean
    onChange: (checked: boolean) => void
    label: string
    description?: string
}

export const Switch: FC<SwitchProps> = ({ id, checked, onChange, label, description }): ReactNode => {
    return (
        <div className='flex items-start justify-between gap-4'>
            <div className='flex flex-col gap-0.5'>
                <label htmlFor={id} className='cursor-pointer text-sm font-medium'>{label}</label>
                {description && <p className='text-sm text-muted-foreground'>{description}</p>}
            </div>

            <button
                id={id}
                type='button'
                role='switch'
                aria-checked={checked}
                onClick={() => onChange(!checked)}
                className={cn(
                    'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                    checked ? 'bg-primary' : 'bg-input'
                )}
            >
                <span
                    className={cn(
                        'inline-block size-5 rounded-full bg-white shadow transition-transform',
                        checked ? 'translate-x-5.5' : 'translate-x-0.5'
                    )}
                />
            </button>
        </div>
    )
}
